import supabase from "@/lib/supabase";
import useAuthStore from "@/store/useAuthStore";
import { getAllQuests, getQuestModes } from "@/lib/questHelper";
import { Quest } from "./schema";
import { getLocalDateString } from "@/lib/utils";

export interface WeeklyTask {
  quest_id: string;
  quest_name: string;
  quest_image: string;
  quest_progress: number;
  weekly_count: number;
  type: string;
}

export interface ScheduleDailyTask {
  quest_id: string;
  quest_name: string;
  quest_image: string;
  quest_subject: string | null | undefined;
  lessons_per_day: number;
  type: string;
}

export interface Schedule {
  id?: string;
  quest_id: string;
  quest_name: string;
  quest_image: string;
  quest_subject: string | null | undefined;
  quest_progress: QuestProgress;
  lessons_per_day: number;
  type: string;
  days: number[];
}

export interface QuestProgress {
  mode_progress: number;
  mode_total: number;
  lesson_progress: number;
  lesson_total: number;
}

const getSchedulesFromDB = async (userId: string) => {
  const { data, error } = await supabase.from('schedule').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  if (error) {
    console.error(error);
  }
  return data;
}

export const getScheduleFromDB = async (userId: string, questId: string) => {
  const { data, error } = await supabase.from('schedule').select('*').eq('user_id', userId).eq('quest_id', questId).maybeSingle();
  if (error && error.code !== 'PGRST116') {
    console.error(error);
  }
  return data;
}

export const getProgressFromDB = async (userId: string, start?: string, end?: string) => {
  let query = supabase.from('user_quest_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('progress', 100);
  if (start && start !== '') query = query.gte('updated_at', start);
  if (end && end !== '') query = query.lt('updated_at', end);
  const { data, error } = await query;
  if (error && error.code === 'PGRST116') { // not found
    return [];
  }
  if (error) {
    console.error(error);
  }
  return data;
}

export const deleteScheduleFromDB = async (scheduleId: string) => {
  const { data, error } = await supabase.from('schedule').delete().eq('id', scheduleId);
  if (error) {
    console.error(error);
  }
  return data;
}

export const saveScheduleToDB = async (schedule: Schedule) => {
  const user = useAuthStore.getState().user;
  if (!user || !user.id) {
    throw new Error('User not found');
  }
  const { data: existing, error: findError } = await supabase
    .from('schedule')
    .select('id')
    .eq('user_id', user.id)
    .eq('quest_id', schedule.quest_id)
    .maybeSingle();

  if (findError && findError.code !== 'PGRST116') {
    // 其他错误
    throw findError;
  }

  let result;
  if (existing?.id) {
    // 已存在，update
    const { data, error } = await supabase
      .from('schedule')
      .update({
        lessons_per_day: schedule.lessons_per_day,
        days: schedule.days,
      })
      .eq('id', existing.id);
    if (error) throw error;
    result = data;
  } else {
    // 不存在，insert
    const { data, error } = await supabase
      .from('schedule')
      .insert({
        user_id: user.id,
        quest_id: schedule.quest_id,
        status: "in-progress",
        lessons_per_day: schedule.lessons_per_day,
        days: schedule.days,
        type: schedule.type,
      });
    if (error) throw error;
    result = data;
  }
  return result;
}

export const getFinishedLessonFromDB = async (userId: string, start?: string, end?: string, modeTypes: string[] = []) => {
  let query = supabase.from('finished_lesson').select('*').eq('user_id', userId);
  if (start && start !== '') query = query.gte('created_at', start);
  if (end && end !== '') query = query.lt('created_at', end);
  if (modeTypes?.length > 0) query = query.in('mode_type', modeTypes);
  const { data, error } = await query;
  if (error && error.code === 'PGRST116') { // not found
    return [];
  }
  if (error) {
    console.error(error);
  }
  return data;
}

const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0 (Sun) - 6 (Sat)
  const diffToSunday = -day; // Sunday is 0
  const sunday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToSunday, 0, 0, 0, 0);
  const nextSunday = new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + 7, 0, 0, 0, 0);
  const utcWeekStart = sunday.toISOString();
  const utcWeekEnd = nextSunday.toISOString();
  return { utcWeekStart, utcWeekEnd };
}

export const getQuestProgress = (quest: Quest, allQuestsProgress: any[] | null): QuestProgress => {
  let totalModes = 0;
  let finishedModes = 0;
  let totalLessons = 0;
  let finishedLessons = 0;
  const { lessonModes, chapterModes } = getQuestModes(quest!);
  if (quest!.chapters && Array.isArray(quest!.chapters)) {
    quest!.chapters.forEach((chapter) => {
      // lesson 层级
      if (chapter.lesson_list && Array.isArray(chapter.lesson_list)) {
        chapter.lesson_list.forEach((lesson) => {
          if (lessonModes && lessonModes.length > 0) {
            totalLessons++;
            totalModes += lessonModes.length;
            const questProgress = allQuestsProgress?.filter(progress => progress.lesson_id === lesson.id && progress.progress === 100);
            if (questProgress) {
              finishedModes += questProgress.filter(progress => lessonModes.includes(progress.mode_type)).length;
              if (lessonModes.every(mode => questProgress.some(progress => progress.mode_type === mode))) {
                finishedLessons++;
              }
            }
          }
        });
      }
      // chapter 层级
      if (chapterModes && chapterModes.length > 0) {
        totalLessons++;
        totalModes += chapterModes.length;
        const questProgress = allQuestsProgress?.filter(progress => progress.chapter_id === chapter.id && progress.lesson_id === "empty" && progress.progress === 100);
        if (questProgress) {
          finishedModes += questProgress.filter(progress => chapterModes.includes(progress.mode_type)).length;
          if (chapterModes.every(mode => questProgress.some(progress => progress.mode_type === mode))) {
            finishedLessons++;
          }
        }
      }
    });
  }
  return {
    mode_progress: finishedModes,
    mode_total: totalModes,
    lesson_progress: finishedLessons,
    lesson_total: totalLessons
  };
}

export const getSchedules = async (allQuests: Quest[], allQuestsProgress: any[] | null): Promise<Schedule[]> => {
  const user = useAuthStore.getState().user;
  if (!user || !user.id) {
    return [];
  }
  const schedules = await getSchedulesFromDB(user.id);
  if (!schedules || !allQuests) {
    return [];
  }
  const scheduleWithQuests = schedules.filter(item => allQuests.some(quest => quest.id === item.quest_id)).map((item) => {
    const quest = allQuests.find((quest) => quest.id === item.quest_id);
    // 计算该 quest 中所有 lesson/chapter 的 mode 总数和已完成的 mode 数量
    const questProgress = getQuestProgress(quest!, allQuestsProgress);
    return {
      id: item.id,
      quest_id: quest!.id,
      quest_name: quest!.title,
      quest_image: quest!.cover_image || "",
      quest_subject: quest!.subject,
      quest_progress: questProgress,
      lessons_per_day: item.lessons_per_day,
      type: item.type,
      days: item.days,
    };
  });
  return scheduleWithQuests;
}

export const getWeeklyTasks = async (userId?: string): Promise<WeeklyTask[]> => {
  if (!userId) {
    const user = useAuthStore.getState().user;
    if (!user || !user.id) {
      return [];
    }
    userId = user.id;
  }
  const schedules = await getSchedulesFromDB(userId);
  if (!schedules) {
    return [];
  }
  const { utcWeekStart, utcWeekEnd } = getWeekRange();
  const finishedLessons = await getFinishedLessonFromDB(userId, utcWeekStart, utcWeekEnd);
  const allQuests = await getAllQuests(false);
  const weeklyTasks = schedules.map((item) => {
    const quest = allQuests.find((quest) => quest.id === item.quest_id);
    const quest_finishedLessons = finishedLessons?.filter(lesson => lesson.quest_id === quest?.id && lesson.mode_type === "empty" && lesson.lesson_id !== "empty").length || 0;
    return {
      quest_id: quest?.id || "",
      quest_name: quest?.title || "",
      quest_image: quest?.cover_image || "",
      quest_progress: quest_finishedLessons || 0,
      weekly_count: item.lessons_per_day * item.days.length,
      type: item.type as string,
    };
  });
  return weeklyTasks || [];
}

const isScheduleIncompleteByDate = (schedule: Schedule, allQuests: Quest[], allQuestsProgress: any[] | null, date: string) => {
  const targetDate = new Date(date);
  const todayLocal = getLocalDateString();
  const questProgress = getQuestProgress(allQuests.find(quest => quest.id === schedule.quest_id)!, allQuestsProgress);
  const incompleteLessons = questProgress.lesson_total - questProgress.lesson_progress;
  const weeklyCount = schedule.lessons_per_day * schedule.days.length;
  const weeks = Math.ceil(incompleteLessons / weeklyCount);
  const today = new Date(todayLocal);
  const day = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  const lastSunday = new Date(today);
  lastSunday.setDate(today.getDate() - day);

  // 预计完成日期
  const completeDate = new Date(lastSunday);
  completeDate.setDate(lastSunday.getDate() + weeks * 7);

  // 如果预计完成日期早于今天，说明未完成
  if (targetDate < completeDate) {
    return true;
  }
  return false;
}

export const getScheduleDailyTasks = (schedules: Schedule[], allQuests: Quest[], allQuestsProgress: any[] | null, date: string): ScheduleDailyTask[] => {
  const tasks: ScheduleDailyTask[] = [];
  const dayOfWeek = new Date(date).getDay();
  schedules.forEach(schedule => {
    if (isScheduleIncompleteByDate(schedule, allQuests, allQuestsProgress, date)) {
      if (schedule.days.includes(dayOfWeek)) {
        tasks.push({
          quest_id: schedule.quest_id,
          quest_name: allQuests.find(quest => quest.id === schedule.quest_id)?.title || "",
          quest_image: allQuests.find(quest => quest.id === schedule.quest_id)?.cover_image || "",
          quest_subject: allQuests.find(quest => quest.id === schedule.quest_id)?.subject || "",
          lessons_per_day: schedule.lessons_per_day,
          type: schedule.type,
        });
      }
    }
  });
  return tasks;
}
